import {div, filler, hbox, span} from '@xh/hoist/cmp/layout';
import {hoistCmp, HoistProps, uses} from '@xh/hoist/core';
import {button} from '@xh/hoist/desktop/cmp/button';
import {textArea} from '@xh/hoist/desktop/cmp/input';
import {Icon} from '@xh/hoist/icon';
import {isEmpty} from 'lodash';
import {aiIcon} from '../../ai/AiButton';
import {stackedPanel} from '../../gridSpec/configurator/Common';
import {SpecAssistantModel, SpecAssistantTurn} from './SpecAssistantModel';
import './SpecAssistant.scss';
import {paperPlaneIcon} from '../../../../core/Icons';

/**
 * AI section of a spec configurator (grid, chart). Its changes land in the same spec (and undo
 * history) as manual edits, and the other configurator sections reflect them immediately.
 */
export const specAssistant = hoistCmp.factory<SpecAssistantModel>({
    model: uses(SpecAssistantModel, {publishMode: 'none'}),
    render({model}) {
        const {specModel, task, turns} = model;

        return stackedPanel({
            title: 'Assistant',
            icon: aiIcon(),
            className: 'stacked-panel spec-assistant',
            headerItems: [
                button({
                    icon: Icon.undo(),
                    minimal: true,
                    disabled: !specModel.canUndo,
                    title: specModel.canUndo ? `Undo: ${specModel.undoLabel}` : 'Undo',
                    onClick: () => specModel.undo()
                }),
                button({
                    icon: Icon.redo(),
                    minimal: true,
                    disabled: !specModel.canRedo,
                    title: specModel.canRedo ? `Redo: ${specModel.redoLabel}` : 'Redo',
                    onClick: () => specModel.redo()
                })
            ],
            items: [
                div({
                    className: 'spec-assistant__turns',
                    ref: model.scrollRef,
                    omit: isEmpty(turns),
                    items: turns.map((t, idx) => turnView({key: idx, turn: t}))
                }),
                div({
                    className: 'spec-assistant__hint',
                    omit: !isEmpty(turns),
                    item: model.isAvailable ? model.hint : 'The assistant is not available on the server yet - use the settings below.'
                }),
                div({
                    className: 'spec-assistant__input',
                    items: [
                        textArea({
                            model,
                            bind: 'prompt',
                            commitOnChange: true,
                            placeholder: `Ask the assistant to change this ${model.kind}...`,
                            width: null,
                            height: 60,
                            disabled: !model.isAvailable,
                            onKeyDown: e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    model.submitAsync();
                                }
                            }
                        }),
                        hbox({
                            className: 'spec-assistant__actions',
                            items: [
                                span({className: 'spec-assistant__status', omit: !task.isPending, item: task.message}),
                                filler(),
                                button({
                                    text: 'Clear',
                                    minimal: true,
                                    omit: isEmpty(turns),
                                    onClick: () => model.clearHistory()
                                }),
                                button({
                                    icon: task.isPending ? Icon.spinner({className: 'fa-spin'}) : paperPlaneIcon(),
                                    text: 'Apply',
                                    intent: 'primary',
                                    disabled: !model.canSubmit || !model.isAvailable,
                                    onClick: () => model.submitAsync()
                                })
                            ]
                        })
                    ]
                })
            ]
        });
    }
});

interface TurnViewProps extends HoistProps {
    turn: SpecAssistantTurn;
}

const turnView = hoistCmp.factory<TurnViewProps>({
    model: false,
    render({turn}) {
        const {instruction, explanation, appliedLabel, rejected, warnings, notes, error, pending} = turn;
        // The user's instruction as a bubble on the right, the assistant's reply below it on the left
        return div({
            className: 'spec-assistant__turn',
            items: [
                div({className: 'spec-assistant__instruction', item: instruction}),
                div({
                    className: 'spec-assistant__reply',
                    items: [
                        div({className: 'spec-assistant__reply-icon', item: aiIcon()}),
                        div({
                            className: 'spec-assistant__reply-body',
                            items: [
                                div({className: 'spec-assistant__pending', omit: !pending, items: [Icon.spinner({className: 'fa-spin'}), ' Thinking...']}),
                                div({className: 'spec-assistant__explanation', omit: !explanation, item: explanation}),
                                div({className: 'spec-assistant__applied', omit: !appliedLabel, items: [Icon.check(), ' ', appliedLabel]}),
                                ...(notes ?? []).map((n, idx) => div({key: `n${idx}`, className: 'spec-assistant__note', items: [Icon.magic(), ' ', n]})),
                                ...(warnings ?? []).map((w, idx) =>
                                    div({key: `w${idx}`, className: 'spec-assistant__rejected', items: [Icon.warning(), ' ', w]})
                                ),
                                ...(rejected ?? []).map((r, idx) =>
                                    div({key: idx, className: 'spec-assistant__rejected', items: [Icon.warning(), ' Skipped: ', r.reason]})
                                ),
                                div({className: 'spec-assistant__error', omit: !error, items: [Icon.error(), ' ', error]})
                            ]
                        })
                    ]
                })
            ]
        });
    }
});
